import React, { useRef, useState, useEffect } from 'react';
import type { Column, CardType } from './KanbanBoard';
import ReactMarkdown from 'react-markdown';

interface WorkBoardCanvasProps {
  columns: Column[];
  cards: { [cardId: string]: CardType };
  boardId?: string;
  userId?: string;
  userProfileMap?: Record<string, { name: string; color: string }>;
  onAddCard?: (colId: string) => void;
  onDeleteCard?: (colId: string, cardId: string) => void;
  onEditCard?: (colId: string, cardId: string) => void;
  onPositionsChange?: (positions: { [cardId: string]: { x: number; y: number } }) => void;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.15;

// Default position generator for new columns
const getDefaultColPos = (colIdx: number) => ({
  x: 80 + colIdx * 340,
  y: 100,
});

const COL_WIDTH = 320;
const COL_HEIGHT = 500;
const COL_LABEL_HEIGHT = 40;

const WorkBoardCanvas: React.FC<WorkBoardCanvasProps> = ({ columns, cards, boardId, userId, userProfileMap, onAddCard, onDeleteCard, onEditCard, onPositionsChange }) => {
  const [zoom, setZoom] = useState(1);
  // Store column positions in local state (by colId)
  const [colPositions, setColPositions] = useState<{ [colId: string]: { x: number; y: number } }>({});
  const [dragging, setDragging] = useState<{ colId: string; offsetX: number; offsetY: number } | null>(null);

  // Initialize positions for columns that don't have one
  useEffect(() => {
    setColPositions(prev => {
      const next = { ...prev };
      columns.forEach((col, colIdx) => {
        if (!next[col.id]) {
          next[col.id] = getDefaultColPos(colIdx);
        }
      });
      return next;
    });
    // eslint-disable-next-line
  }, [columns.map(c => c.id).join(",")]);

  // Drag handlers for columns
  const handleColMouseDown = (e: React.MouseEvent, colId: string) => {
    const colDiv = e.currentTarget as HTMLDivElement;
    const rect = colDiv.getBoundingClientRect();
    setDragging({
      colId,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setColPositions(pos => ({
      ...pos,
      [dragging.colId]: {
        x: (e.clientX - dragging.offsetX - 60) / zoom,
        y: (e.clientY - dragging.offsetY - 60) / zoom,
      },
    }));
  };
  const handleMouseUp = () => setDragging(null);

  // Canvas size
  const canvasWidth = Math.max(1200, columns.length * 340);
  const canvasHeight = 1200;

  // Render columns as draggable nodes
  const renderColumns = () => (
    columns.map((col, idx) => {
      const pos = colPositions[col.id] || getDefaultColPos(idx);
      return (
        <div
          key={col.id}
          style={{
            position: 'absolute',
            left: pos.x,
            top: pos.y,
            width: COL_WIDTH,
            minHeight: COL_LABEL_HEIGHT + 80,
            background: '#f1f5f9',
            borderRadius: 12,
            boxShadow: '0 2px 8px #0001',
            zIndex: dragging?.colId === col.id ? 10 : 2,
            cursor: dragging?.colId === col.id ? 'grabbing' : 'grab',
            userSelect: 'none',
            transition: dragging?.colId === col.id ? 'none' : 'box-shadow 0.2s',
          }}
          onMouseDown={e => handleColMouseDown(e, col.id)}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          <div
            style={{
              width: '100%',
              height: COL_LABEL_HEIGHT,
              background: '#e2e8f0',
              borderRadius: '12px 12px 0 0',
              textAlign: 'center',
              lineHeight: COL_LABEL_HEIGHT + 'px',
              fontWeight: 700,
              fontSize: 18,
              boxShadow: '0 2px 8px #0001',
            }}
          >
            {col.title}
            {onAddCard && (
              <button className="btn btn-xs ml-2" style={{ float: 'right', marginTop: 6 }} onClick={e => { e.stopPropagation(); onAddCard(col.id); }}>+ Add Card</button>
            )}
          </div>
          <div style={{ padding: 8 }}>
            {col.cardIds.map(cardId => {
              const card = cards[cardId];
              if (!card) return null;
              return (
                <div
                  key={cardId}
                  className="bg-white rounded-lg shadow p-3 mb-2 flex flex-col gap-1 relative border border-gray-200 cursor-pointer hover:bg-slate-50 transition"
                  style={{ borderLeft: `6px solid ${card.color || '#64748b'}` }}
                  onDoubleClick={() => onEditCard?.(col.id, cardId)}
                >
                  <button
                    className="absolute top-2 right-2 text-xs text-gray-400 hover:text-red-600"
                    style={{ zIndex: 20 }}
                    onClick={ev => {
                      ev.stopPropagation();
                      onDeleteCard?.(col.id, cardId);
                    }}
                    title="Delete card"
                  >
                    ×
                  </button>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg font-bold text-slate-800 truncate">{card.title}</span>
                    <span className="ml-auto px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-500">
                      {card.type === 'vuln' ? '🐞 Vuln' : card.type === 'tool' ? '🔧 Tool' : card.type === 'payload' ? '💥 Payload' : card.type === 'resource' ? '🔗 Link' : '📝 Note'}
                    </span>
                  </div>
                  {/* Card content preview or badges */}
                  {card.type === 'vuln' && card.content?.severity && (
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold text-white ${card.content.severity === 'Critical' ? 'bg-red-600' : card.content.severity === 'High' ? 'bg-orange-500' : card.content.severity === 'Medium' ? 'bg-yellow-400 text-slate-900' : 'bg-green-500'}`}>{card.content.severity}</span>
                  )}
                  {/* Tags */}
                  {card.tags && card.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {card.tags.map(tag => (
                        <span key={tag} className="bg-slate-200 rounded px-2 py-0.5 text-xs">{tag}</span>
                      ))}
                    </div>
                  )}
                  {/* Markdown preview for note, vuln.description, vuln.poc */}
                  {card.type === 'note' && (
                    <div className="mt-1 text-xs text-slate-700 line-clamp-4 break-words">
                      {typeof card.content === 'string' ? card.content : card.content?.note || ''}
                    </div>
                  )}
                  {card.type === 'vuln' && (
                    <>
                      {card.content?.description && (
                        <div className="mt-1 text-xs text-slate-700 line-clamp-3 break-words">
                          {card.content.description}
                        </div>
                      )}
                      {card.content?.poc && (
                        <div className="mt-1 text-xs text-slate-700 line-clamp-2 break-words">
                          {card.content.poc}
                        </div>
                      )}
                    </>
                  )}
                  {/* Custom fields */}
                  {Array.isArray(card.content?.customFields) && card.content.customFields.length > 0 && (
                    <div className="mt-1 flex flex-col gap-1">
                      {card.content.customFields.map((f: any, i: number) => (
                        <div key={i} className="flex gap-2 text-xs">
                          <span className="font-semibold text-slate-500">{f.key}:</span>
                          <span className="text-slate-700">{f.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    })
  );

  // Save/load positions from localStorage
  useEffect(() => {
    if (!userId || !boardId) return;
    const key = `huntersBoardColPositions_${userId}_${boardId}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        setColPositions(JSON.parse(saved));
      } catch {}
    }
  }, [userId, boardId]);
  useEffect(() => {
    if (!userId || !boardId) return;
    const key = `huntersBoardColPositions_${userId}_${boardId}`;
    localStorage.setItem(key, JSON.stringify(colPositions));
    if (onPositionsChange) onPositionsChange(colPositions);
  }, [colPositions, userId, boardId]);

  const renderUserBadge = (userId?: string) => {
    if (!userId || !userProfileMap) return null;
    const { name, color } = userProfileMap[userId] || { name: userId, color: '#64748b' };
    const initial = name[0]?.toUpperCase() || '?';
    return (
      <span
        className="ml-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border border-white shadow"
        style={{ background: color, color: '#fff' }}
        title={`Last edited by ${name}`}
      >
        {initial}
      </span>
    );
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Zoom controls */}
      <div style={{ position: 'absolute', top: 0, right: 24, zIndex: 10, display: 'flex', gap: 8 }}>
        <button className="btn btn-xs" onClick={() => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP))}>-</button>
        <span className="text-xs font-mono px-2">{(zoom * 100).toFixed(0)}%</span>
        <button className="btn btn-xs" onClick={() => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP))}>+</button>
      </div>
      <div style={{ overflow: 'auto', width: '100%', height: 'calc(100vh - 120px)' }}>
        <div
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            transition: 'transform 0.2s',
            minWidth: 900,
          }}
        >
          <button className="btn btn-sm mb-2" onClick={onAddCard ? () => onAddCard('') : undefined}>+ Add Column</button>
          <div
            className="grid gap-4 w-full"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              alignItems: 'start',
            }}
          >
            {columns.map((col, colIdx) => (
              <div
                key={col.id}
                className="bg-gray-100 rounded-xl p-3 min-w-[300px] max-w-[340px] flex flex-col shadow-md mb-4"
                style={{ flex: '1 1 320px', boxSizing: 'border-box' }}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-lg">{col.title}</span>
                  {onAddCard && (
                    <button className="btn btn-xs ml-2" onClick={() => onAddCard(col.id)}>+ Add Card</button>
                  )}
                </div>
                <div className="flex-1">
                  {col.cardIds.map((cardId) => {
                    const card = cards[cardId];
                    if (!card) return null;
                    return (
                      <div
                        key={cardId}
                        className="bg-white rounded-lg shadow p-3 mb-2 flex flex-col gap-1 relative border border-gray-200 cursor-pointer hover:bg-slate-50 transition"
                        style={{ borderLeft: `6px solid ${card.color || '#64748b'}` }}
                        onClick={e => {
                          if ((e.target as HTMLElement).closest('button')) return;
                          onEditCard?.(col.id, card.id);
                        }}
                      >
                        <button
                          className="absolute top-2 right-2 text-xs text-gray-400 hover:text-red-600"
                          onClick={ev => { ev.stopPropagation(); onDeleteCard?.(col.id, card.id); }}
                          title="Delete card"
                        >
                          ×
                        </button>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg font-bold text-slate-800 truncate">{card.title}</span>
                          {renderUserBadge(card.lastEditedBy)}
                          <span className="ml-auto px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-500">
                            {card.type === 'vuln' ? '🐞 Vuln' : card.type === 'tool' ? '🔧 Tool' : card.type === 'payload' ? '💥 Payload' : card.type === 'resource' ? '🔗 Link' : '📝 Note'}
                          </span>
                        </div>
                        {/* Card content preview or badges */}
                        {card.type === 'vuln' && card.content?.severity && (
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold text-white ${card.content.severity === 'Critical' ? 'bg-red-600' : card.content.severity === 'High' ? 'bg-orange-500' : card.content.severity === 'Medium' ? 'bg-yellow-400 text-slate-900' : 'bg-green-500'}`}>{card.content.severity}</span>
                        )}
                        {/* Tags */}
                        {card.tags && card.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {card.tags.map(tag => (
                              <span key={tag} className="bg-slate-200 rounded px-2 py-0.5 text-xs">{tag}</span>
                            ))}
                          </div>
                        )}
                        {/* Markdown preview for note, vuln.description, vuln.poc */}
                        {card.type === 'note' && (
                          <div className="mt-1 text-xs text-slate-700 line-clamp-4 break-words">
                            {typeof card.content === 'string' ? card.content : card.content?.note || ''}
                          </div>
                        )}
                        {card.type === 'vuln' && (
                          <>
                            {card.content?.description && (
                              <div className="mt-1 text-xs text-slate-700 line-clamp-3 break-words">
                                {card.content.description}
                              </div>
                            )}
                            {card.content?.poc && (
                              <div className="mt-1 text-xs text-slate-700 line-clamp-2 break-words">
                                {card.content.poc}
                              </div>
                            )}
                          </>
                        )}
                        {/* Custom fields */}
                        {Array.isArray(card.content?.customFields) && card.content.customFields.length > 0 && (
                          <div className="mt-1 flex flex-col gap-1">
                            {card.content.customFields.map((f: any, i: number) => (
                              <div key={i} className="flex gap-2 text-xs">
                                <span className="font-semibold text-slate-500">{f.key}:</span>
                                <span className="text-slate-700">{f.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkBoardCanvas; 